// React Imports
import { ReactElement, useEffect, useState } from "react";

// Third Party Libraries Imports
import { Link, useParams } from "react-router-dom";
import moment from "moment";
import { ColumnsType } from "antd/es/table";
import clsx from "clsx";

// Local Imports
import CustomModal from "../../components/custom-modal/custom-modal";
import BreadcrumbComponent from "../../components/breadcrumb-component/breadcrumb-component";
import TakeRedemptionsActionService from "./take-redemptions-action.service";
import CollapseComponent from "../../components/collapse.component";
import TakeAction from "../../components/take-action/take-action";
import { extractToEntity, formatNumber } from "../../utils/helper/functions";
import CustomTable from "../../components/custom-table/custom-table";
import { useSelector } from "react-redux";
import TakeActionForm from "../withdrawal/components/take-action-form/take-action-form";
import { Form } from "antd";
import WithdrawalService from "../withdrawal/withdrawal.service";
import CustomInput from "../../components/custom-input/custom-input.component";
import Label from "../../components/label/label.component";
import TextArea from "antd/es/input/TextArea";
import PdfViewer from "src/components/pdf-viewer";

const groups: any = {
  OPS: "Operation",
  AMD: "AMD",
  WMD: "WMD",
};

const TakeRedemptionsAction: React.FC = (): ReactElement => {
  const { redemptionId }: { [key: string]: string | undefined } = useParams();
  const takeRedemptionsActionService = new TakeRedemptionsActionService();
  const takeWithdrawalActionService = new WithdrawalService();
  const user = useSelector((state: any) => state?.user);
  const [form] = Form.useForm();
  const values = Form.useWatch([], form);
  const [submitForm, setSubmitForm] = useState(false);
  const [openApproveModal, setOpenApproveModal] = useState<boolean>(false);
  const [openRejectModal, setOpenRejectModal] = useState<boolean>(false);
  const [openRejectWithdrawModal, setOpenRejectWithdrawModal] =
    useState<boolean>(false);
  const [openApproveWithdrawModal, setOpenApproveWithdrawModal] =
    useState<boolean>(false);
  const [redemptionDetailsData, setRedemptionDetailsData] = useState<any>();
  const [openPdfViewerModal, setOpenPdfViewerModal] = useState<boolean>(false);
  const [selectedDocument, setSelectedDocument] = useState<any>({});

  useEffect(() => {
    form.validateFields({ validateOnly: true }).then(
      () => {
        setSubmitForm(true);
      },
      () => {
        setSubmitForm(false);
      }
    );
  }, [values]);

  useEffect(() => {
    fetchRedemptionDetailsData();
  }, []);

  const onTakeAction = async (action: string, value: string) => {
    const response: string | undefined =
      await takeRedemptionsActionService.takeAction(
        redemptionId,
        action,
        value
      );
    if (response === "success") {
      fetchRedemptionDetailsData();
      setOpenApproveModal(false);
      setOpenRejectModal(false);
    }
  };

  const fetchRedemptionDetailsData = async (): Promise<void> => {
    const data = await takeRedemptionsActionService.getRedemptionDetailsData(
      redemptionId
    );

    if (data !== undefined) {
      setRedemptionDetailsData(data);
    }
  };

  const columns: ColumnsType<any> = [
    {
      title: "#",
      dataIndex: "id",
      key: "id",
      fixed: "left",
      render: (_: any, __: any, index: number) => <span>{index + 1}</span>,
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      fixed: "left",
      render: (_: any, record: any) => (
        <span>{formatNumber(record?.convertedAmount)}</span>
      ),
    },
    {
      title: "Currency",
      dataIndex: "currency",
      key: "currency",
      fixed: "left",
      render: (_: any, record: any) => <span>{record?.originalCurrency}</span>,
    },
  ];

  const onApproveWithdrawal = async (values: any) => {
    const response: string | undefined =
      await takeWithdrawalActionService.takeAction(
        redemptionDetailsData?.withdrawId,
        "approve",
        values?.statusReason,
        values?.notes,
        values?.transactionId
      );
    if (response === "success") {
      fetchRedemptionDetailsData();
      setOpenApproveWithdrawModal(false);
    }
  };

  const onRejectWithdrawal = async (values: any) => {
    const response: string | undefined =
      await takeWithdrawalActionService.takeAction(
        redemptionDetailsData?.withdrawId,
        "reject",
        values?.statusReason,
        values?.notes,
        values?.transactionId
      );
    if (response === "success") {
      fetchRedemptionDetailsData();
      setOpenRejectWithdrawModal(false);
    }
  };

  return (
    redemptionDetailsData && (
      <section className="flex flex-col">
        <BreadcrumbComponent />
        <div className="flex flex-col pb-7 flex-grow">
          <CollapseComponent
            header={
              <div className="flex gap-3 items-center">
                <span className="w-[5px] bg-blue-500 h-[25px] rounded-[10px]"></span>
                <h3 className=" text-xl font-semibold">Portfolio details</h3>
              </div>
            }
          >
            <div className="flex gap-5 flex-wrap mb-5">
              <div className="w-[32%] flex flex-col">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Account#
                </p>
                <Link
                  to={`/home/business-account/${redemptionDetailsData?.portfolio?.account?.id}/${redemptionDetailsData?.portfolio?.account?.activeKycId}/details`}
                  className=" text-base font-semibold text-lightGray-200 underline"
                >
                  {redemptionDetailsData?.portfolio?.account?.accountCode}
                </Link>
              </div>
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Account type
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {
                    redemptionDetailsData?.portfolio?.account?.clientType
                      ?.description
                  }{" "}
                  -{" "}
                  {
                    redemptionDetailsData?.portfolio?.account?.clientSubType
                      ?.description
                  }
                </span>
              </div>
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Account status
                </p>
                <span className=" text-base font-semibold w-fit px-3 py-1 rounded-md bg-green-100 text-white">
                  {
                    redemptionDetailsData?.portfolio?.account?.accountStatus
                      ?.description
                  }
                </span>
              </div>

              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Portfolio#
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {redemptionDetailsData?.portfolio?.accountCode}
                </span>
              </div>
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Portfolio type
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {redemptionDetailsData?.portfolio?.managementType}
                </span>
              </div>
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Portfolio status
                </p>
                <span
                  className={clsx(
                    "text-base font-semibold w-fit px-3 py-1 rounded-md text-white",
                    redemptionDetailsData?.portfolio?.status === "Opened"
                      ? "bg-green-100"
                      : redemptionDetailsData?.portfolio?.status === "Rejected"
                      ? "bg-red-200"
                      : "bg-yellow-500"
                  )}
                >
                  {redemptionDetailsData?.portfolio?.status === "Pending"
                    ? "New"
                    : redemptionDetailsData?.portfolio?.status}
                </span>
              </div>
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Portfolio currency
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {redemptionDetailsData?.portfolio?.portfolioCurrency || "-"}
                </span>
              </div>
              {redemptionDetailsData?.portfolio?.openDate && (
                <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                  <p className=" text-black-300 text-xs font-normal mb-[10px]">
                    Open date
                  </p>
                  <span className=" text-base font-semibold text-lightGray-200">
                    {moment(redemptionDetailsData?.portfolio?.openDate).format(
                      "DD MMM, YYYY"
                    )}
                  </span>
                </div>
              )}
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Wealth manager
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {redemptionDetailsData?.account?.wealthManager?.ae1 || "-"}
                </span>
              </div>
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Wealth manager email
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {redemptionDetailsData?.account?.wealthManager?.ae1Email ||
                    "-"}
                </span>
              </div>
              {redemptionDetailsData?.account?.wealthManager?.ae2 && (
                <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                  <p className=" text-black-300 text-xs font-normal mb-[10px]">
                    Wealth manager
                  </p>
                  <span className=" text-base font-semibold text-lightGray-200">
                    {redemptionDetailsData?.account?.wealthManager?.ae2 || "-"}
                  </span>
                </div>
              )}
              {redemptionDetailsData?.account?.wealthManager?.ae2 && (
                <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                  <p className=" text-black-300 text-xs font-normal mb-[10px]">
                    Wealth manager email
                  </p>
                  <span className=" text-base font-semibold text-lightGray-200">
                    {redemptionDetailsData?.account?.wealthManager?.ae2Email ||
                      "-"}
                  </span>
                </div>
              )}
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Online trading
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {redemptionDetailsData?.portfolio?.onlineTradeSys
                    ? "Yes"
                    : "No"}
                </span>
              </div>

              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Pledged
                </p>
                <span
                  className={clsx(
                    "rounded-md w-fit",
                    redemptionDetailsData?.portfolio?.portfolioDisclaimer
                      ? "bg-[#FFAF00] text-white text-sm font-medium p-2"
                      : "bg-transparent text-lightGray-200 font-semibold text-base p-0"
                  )}
                >
                  {redemptionDetailsData?.portfolio?.portfolioDisclaimer
                    ? extractToEntity(
                        redemptionDetailsData?.portfolio?.portfolioDisclaimer
                      )
                    : "No"}
                </span>
              </div>
            </div>
          </CollapseComponent>

          <CollapseComponent
            header={
              <div className="flex gap-3 items-center">
                <span className="w-[5px] bg-blue-500 h-[25px] rounded-[10px]"></span>
                <h3 className=" text-xl font-semibold">
                  Redemption details{" "}
                  {redemptionDetailsData?.status === "Approved" ||
                  redemptionDetailsData?.status === "Rejected" ||
                  redemptionDetailsData?.status === "PENDING_WITHDRAW" ? (
                    <span
                      className={clsx(
                        "text-base font-semibold w-fit px-3 py-2 ms-3 rounded-md  text-white",
                        redemptionDetailsData?.status === "Approved" ||
                          redemptionDetailsData?.status === "PENDING_WITHDRAW"
                          ? "bg-green-100"
                          : "bg-red-200"
                      )}
                    >
                      {redemptionDetailsData?.status === "PENDING_WITHDRAW"
                        ? "Pending withdraw"
                        : redemptionDetailsData?.status}
                    </span>
                  ) : (
                    ""
                  )}
                </h3>
              </div>
            }
          >
            <div className="flex gap-5 flex-wrap mb-5">
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Fund name
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {redemptionDetailsData?.fundNameEn}
                </span>
              </div>
              <div className="w-[32%] flex flex-col">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Redemption amount
                </p>
                <span className=" text-base font-semibold text-lightGray-200 mb-5">
                  {formatNumber(redemptionDetailsData?.redemptionAmount)}
                </span>
              </div>
              <div className="w-[32%] flex flex-col">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Redemption currency
                </p>
                <span className=" text-base font-semibold text-lightGray-200 mb-5">
                  {redemptionDetailsData?.redemptionCurrency}
                </span>
              </div>
              {redemptionDetailsData?.fromAvailableCash?.length ? (
                <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                  <div className="w-full">
                    <CustomTable
                      columns={columns}
                      data={redemptionDetailsData?.fromAvailableCash}
                      showPagination={false}
                    />
                  </div>
                </div>
              ) : (
                ""
              )}
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Redemption unit
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {redemptionDetailsData?.redemptionUnit}
                </span>
              </div>
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Full amount
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {redemptionDetailsData?.fullAmount ? "Yes" : "No"}
                </span>
              </div>

              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Request date
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {moment(redemptionDetailsData?.createdAt).format(
                    "DD MMM, YYYY hh:mm A"
                  )}
                </span>
              </div>

              {redemptionDetailsData?.status === "Approved" ||
                (redemptionDetailsData?.status === "PENDING_WITHDRAW" && (
                  <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                    <p className=" text-black-300 text-xs font-normal mb-[10px]">
                      Prospero redemption transaction ID
                    </p>
                    <span className=" text-base font-semibold text-lightGray-200">
                      {redemptionDetailsData?.transactionId}
                    </span>
                  </div>
                ))}

              {(redemptionDetailsData?.status === "Approved" ||
                redemptionDetailsData?.status === "Rejected" ||
                redemptionDetailsData?.status === "PENDING_WITHDRAW") && (
                <>
                  <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                    <p className=" text-black-300 text-xs font-normal mb-[10px]">
                      {redemptionDetailsData?.status === "Approved"
                        ? "Prospero deposit transaction ID"
                        : "Status reason"}
                    </p>
                    <span className=" text-base font-semibold text-lightGray-200">
                      {redemptionDetailsData?.status === "Approved"
                        ? redemptionDetailsData?.transactionId
                        : redemptionDetailsData?.statusReason}
                    </span>
                  </div>
                  <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                    <p className=" text-black-300 text-xs font-normal mb-[10px]">
                      Action by
                    </p>
                    <span className=" text-base font-semibold text-lightGray-200">
                      {redemptionDetailsData?.actionBy?.name}
                    </span>
                  </div>
                  <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                    <p className=" text-black-300 text-xs font-normal mb-[10px]">
                      Action date
                    </p>
                    <span className=" text-base font-semibold text-lightGray-200">
                      {moment(redemptionDetailsData?.actionDate).format(
                        "DD MMM, YYYY hh:mm A"
                      )}
                    </span>
                  </div>
                </>
              )}
            </div>
            {redemptionDetailsData?.agreementPdf ? (
              <div className="view flex align-center">
                <img
                  src={"/images/info.svg"}
                  alt="view info portfolio agreement"
                />

                <span
                  className="underline cursor-pointer text-blue-500"
                  onClick={() => {
                    setSelectedDocument(redemptionDetailsData?.agreementPdf);
                    setOpenPdfViewerModal(true);
                  }}
                >
                  View redemption agreement
                </span>
              </div>
            ) : (
              ""
            )}
            {redemptionDetailsData?.status === "Pending" &&
              user?.permissions?.includes("request_redemption_approver") && (
                <div className="flex gap-7 justify-end items-center">
                  <button
                    onClick={() => setOpenRejectModal(true)}
                    className="rounded-lg bg-white w-1/2 md:w-fit px-0 md:px-14 py-3 text-center outline-0 cursor-pointer text-xs md:text-sm font-semibold border-[1px] border-blue-500 text-blue-500"
                  >
                    Reject
                  </button>
                  <button
                    onClick={() => setOpenApproveModal(true)}
                    className="rounded-lg bg-blue-500 w-1/2 md:w-fit px-0 md:px-14 py-3 text-center outline-0 cursor-pointer text-xs md:text-sm font-semibold border-[1px] border-blue-500 text-white"
                  >
                    Approve redemption
                  </button>
                </div>
              )}
          </CollapseComponent>
          {redemptionDetailsData?.withdraw && (
            <CollapseComponent
              header={
                <div className="flex gap-3 items-center">
                  <span className="w-[5px] bg-blue-500 h-[25px] rounded-[10px]"></span>
                  <h3 className=" text-xl font-semibold">
                    Withdraw details{" "}
                    {redemptionDetailsData?.withdraw.status === "Approved" ||
                    redemptionDetailsData?.withdraw?.status === "Rejected" ? (
                      <span
                        className={clsx(
                          "text-base font-semibold w-fit px-3 py-2 ms-3 rounded-md  text-white",
                          redemptionDetailsData?.withdraw?.status === "Approved"
                            ? "bg-green-100"
                            : "bg-red-200"
                        )}
                      >
                        {redemptionDetailsData?.withdraw?.status}
                      </span>
                    ) : (
                      ""
                    )}
                  </h3>
                </div>
              }
            >
              <div className="flex gap-5 flex-wrap mb-5">
                <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                  <p className=" text-black-300 text-xs font-normal mb-[10px]">
                    Total withdrawal amount
                  </p>
                  <span className=" text-base font-semibold text-lightGray-200">
                    {redemptionDetailsData?.withdraw?.withdrawAmount}
                  </span>
                </div>
                <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                  <p className=" text-black-300 text-xs font-normal mb-[10px]">
                    Withdrawal currency
                  </p>
                  <span className=" text-base font-semibold text-lightGray-200">
                    {redemptionDetailsData?.withdraw?.withdrawCurrency}
                  </span>
                </div>

                <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                  <p className=" text-black-300 text-xs font-normal mb-[10px]">
                    All cash
                  </p>
                  <span className=" text-base font-semibold text-lightGray-200">
                    {redemptionDetailsData?.withdraw?.fullAmount ? "Yes" : "No"}
                  </span>
                </div>
                <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                  <p className=" text-black-300 text-xs font-normal mb-[10px]">
                    Beneficiary name
                  </p>
                  <span className=" text-base font-semibold text-lightGray-200">
                    {
                      redemptionDetailsData?.withdraw?.bankAccount
                        ?.beneficiaryName
                    }
                  </span>
                </div>
                <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                  <p className=" text-black-300 text-xs font-normal mb-[10px]">
                    Iban
                  </p>
                  <span className=" text-base font-semibold text-lightGray-200">
                    {redemptionDetailsData?.withdraw?.bankAccount?.ibanNumber}
                  </span>
                </div>
                <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                  <p className=" text-black-300 text-xs font-normal mb-[10px]">
                    Swift code
                  </p>
                  <span className=" text-base font-semibold text-lightGray-200">
                    {redemptionDetailsData?.withdraw?.bankAccount?.swiftCode}
                  </span>
                </div>
                <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                  <p className=" text-black-300 text-xs font-normal mb-[10px]">
                    Request date
                  </p>
                  <span className=" text-base font-semibold text-lightGray-200">
                    {moment(redemptionDetailsData?.withdraw?.createdAt).format(
                      "DD MMM, YYYY hh:mm A"
                    )}
                  </span>
                </div>

                {redemptionDetailsData?.withdraw?.transactionId && (
                  <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                    <p className=" text-black-300 text-xs font-normal mb-[10px]">
                      Prospero withdraw transaction ID
                    </p>
                    <span className=" text-base font-semibold text-lightGray-200">
                      {redemptionDetailsData?.withdraw?.transactionId || "-"}
                    </span>
                  </div>
                )}
                {redemptionDetailsData?.withdraw?.reviewHistory?.length &&
                  redemptionDetailsData?.withdraw?.reviewHistory?.map(
                    (item: any, index: number) => (
                      <div
                        className="flex w-full gap-5 flex-wrap mb-5"
                        key={index}
                      >
                        <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                          <p className=" text-black-300 text-xs font-normal mb-[10px]">
                            {`${groups[item.group]} team notes`}
                          </p>
                          <span className=" text-base font-semibold text-lightGray-200">
                            {item?.notes || "-"}
                          </span>
                        </div>
                        <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                          <p className=" text-black-300 text-xs font-normal mb-[10px]">
                            {`${groups[item.group]} team action by`}
                          </p>
                          <span className=" text-base font-semibold text-lightGray-200">
                            {item?.actionBy?.name}
                          </span>
                        </div>
                        <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                          <p className=" text-black-300 text-xs font-normal mb-[10px]">
                            {`${groups[item.group]} team action date`}
                          </p>
                          <span className=" text-base font-semibold text-lightGray-200">
                            {moment(item?.actionDate).format(
                              "DD MMM, YYYY hh:mm A"
                            )}
                          </span>
                        </div>
                      </div>
                    )
                  )}
              </div>
              {redemptionDetailsData?.agreementPdf ? (
                <div className="view flex align-center">
                  <img
                    src={"/images/info.svg"}
                    alt="view info portfolio agreement"
                  />

                  <span
                    className="underline cursor-pointer text-blue-500"
                    onClick={() => {
                      setSelectedDocument(redemptionDetailsData?.agreementPdf);
                      setOpenPdfViewerModal(true);
                    }}
                  >
                    View redemption form
                  </span>
                </div>
              ) : (
                ""
              )}
              {redemptionDetailsData?.withdraw?.approvalStep === "OPS" &&
                user.withdrawGroup === "OPS" &&
                redemptionDetailsData?.status === "PENDING_WITHDRAW" &&
                user?.permissions?.includes("request_withdrawal_approver") && (
                  <div className="flex gap-7 justify-end items-center">
                    <button
                      onClick={() => setOpenRejectWithdrawModal(true)}
                      className="rounded-lg bg-white w-1/2 md:w-fit px-0 md:px-14 py-3 text-center outline-0 cursor-pointer text-xs md:text-sm font-semibold border-[1px] border-blue-500 text-blue-500"
                    >
                      Reject
                    </button>
                    <button
                      onClick={() => setOpenApproveWithdrawModal(true)}
                      className="rounded-lg bg-blue-500 w-1/2 md:w-fit px-0 md:px-14 py-3 text-center outline-0 cursor-pointer text-xs md:text-sm font-semibold border-[1px] border-blue-500 text-white"
                    >
                      Approve withdrawal
                    </button>
                  </div>
                )}
            </CollapseComponent>
          )}
        </div>

        <CustomModal
          isVisible={openApproveModal}
          centered
          width={450}
          onClose={() => {
            setOpenApproveModal(false);
          }}
        >
          <TakeAction
            label="Prospero deposit transaction ID"
            action="approve"
            fieldName="transactionId"
            errorName="deposit transaction ID"
            onCancel={() => setOpenApproveModal(false)}
            onSubmit={(value) => onTakeAction("approve", value)}
          />
        </CustomModal>

        <CustomModal
          isVisible={openRejectModal}
          centered
          width={450}
          onClose={() => {
            setOpenRejectModal(false);
          }}
        >
          <TakeAction
            label="Rejection reason"
            action="reject"
            fieldName="statusReason"
            errorName="reason"
            onCancel={() => setOpenRejectModal(false)}
            onSubmit={(value) => onTakeAction("reject", value)}
          />
        </CustomModal>

        <CustomModal
          isVisible={openApproveWithdrawModal}
          centered
          width={450}
          onClose={() => {
            setOpenApproveWithdrawModal(false);
          }}
        >
          <TakeActionForm>
            <Form form={form} onFinish={onApproveWithdrawal}>
              {user?.withdrawGroup === "OPS" && (
                <div className="fields flex flex-col">
                  <Label text="Prospero withdraw transaction ID" />
                  <Form.Item
                    name="transactionId"
                    rules={[
                      {
                        required: true,
                        message: `Please input withdraw transaction ID!`,
                      },
                    ]}
                  >
                    <CustomInput
                      placeholder={`Enter withdraw transaction ID`}
                      type="number"
                    />
                  </Form.Item>
                </div>
              )}
              <div className="fields flex flex-col">
                <Label
                  text={
                    user?.withdrawGroup === "AMD"
                      ? "AMD team notes"
                      : user?.withdrawGroup === "WMD"
                      ? "WMD team notes"
                      : "Operation team notes"
                  }
                />
                <Form.Item
                  name="notes"
                  rules={[
                    {
                      required: true,
                      message: `Please input notes!`,
                    },
                  ]}
                >
                  <TextArea rows={4} placeholder={`Enter notes`} />
                </Form.Item>
              </div>
              <div className="buttons-container flex align-center justify-space-between">
                <button
                  className="cancel"
                  type="button"
                  onClick={() => setOpenApproveWithdrawModal(false)}
                >
                  Cancel
                </button>
                <button disabled={!submitForm}>Submit</button>
              </div>
            </Form>
          </TakeActionForm>
        </CustomModal>

        <CustomModal
          isVisible={openRejectWithdrawModal}
          centered
          width={450}
          onClose={() => {
            setOpenRejectWithdrawModal(false);
          }}
        >
          <TakeActionForm>
            <Form form={form} onFinish={onRejectWithdrawal}>
              <div className="fields flex flex-col">
                <Label text="Rejection reason" />
                <Form.Item
                  name="notes"
                  rules={[
                    {
                      required: true,
                      message: `Please input Reason!`,
                    },
                  ]}
                >
                  <TextArea rows={4} placeholder={`Enter Reason`} />
                </Form.Item>
              </div>
              <div className="buttons-container flex align-center justify-space-between">
                <button
                  className="cancel"
                  type="button"
                  onClick={() => setOpenRejectWithdrawModal(false)}
                >
                  Cancel
                </button>
                <button disabled={!submitForm}>Submit</button>
              </div>
            </Form>
          </TakeActionForm>
        </CustomModal>

        <CustomModal
          isVisible={openPdfViewerModal}
          centered
          width={1100}
          showClose={false}
          onClose={() => {
            setOpenPdfViewerModal(false);
          }}
          className="edit-modal"
        >
          <div className="flex flex-col items-center justify-center">
            <PdfViewer fileUrl={selectedDocument} />
          </div>
        </CustomModal>
      </section>
    )
  );
};

export default TakeRedemptionsAction;
